import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import Card from "react-bootstrap/Card";
import InputGroup from "react-bootstrap/InputGroup";
import {SelectBox} from "devextreme-react";
import {Column, DataGrid, Paging} from "devextreme-react/data-grid";
import apiClient from "../../services/axios";
import LoaderService from "../../RetroAssets.System/LoaderService";

// Earliest year offered in the selector. Nothing in the portfolio is eligible
// before this.
const FIRST_YEAR = 2007;

// Grid sizing. The property listing hands DevExtreme a pixel height - it does
// not use a CSS rule, a calc() or height: '100%' - and that mechanism is kept
// here. What is not kept is where the number comes from. The listing takes it
// from cl.setDataGridHeight(), which is
//
//     document.getElementsByClassName('d-flex flex-column wrapper')[0]
//         .offsetHeight - 300
//
// (RetroAssets.System/Utilities.js:196-199), less a further 23 at the call
// site. That 300 is a constant calibrated against the listing's chrome: page
// title row, tab strip, grid. This screen carries a summary strip above the
// tabs and a footnote below the grid, so the constant overshoots by roughly
// the height of both and the page grows a scrollbar. It is also measured once,
// on data arrival, and never again, so it survives neither a resize nor the
// summary strip stacking below 1200px nor the tab strip wrapping.
//
// Everything variable below is therefore measured at layout time: the top of
// the grid itself, everything drawn below it, and the fixed bottom navigation
// bar. The constants below are used only before the grid exists - during a load
// or an error - and each one is a named stylesheet rule.
const MIN_GRID_HEIGHT = 200;
const NOTE_MARGIN = 8;          // footnote mt-2
const CARD_PADDING = 16;        // Card p-3, bottom half
const WRAPPER_PADDING = 16;     // div.main-wrapper p-3, bottom half
const CONTENT_TAIL = 56;        // .content-wrapper padding-bottom, global.scss:10-13

// Tab keys. Company tabs key on OwnerECO_ID, so these two must not collide with
// a GUID.
const TAB_ALL = "ALL";
const TAB_UNASSIGNED = "UNASSIGNED";

// GET /api/Property/GetPropertyOccupancy returns Data only - there is no
// VM_Layout row behind this shape, so the columns below are hand coded and the
// PropertyListing Settings indexing chain does not apply here.
//
//   [ {"Data": [ ...rows... ]}, {"Message": "..."} ]
//
// One flagged row set, not two. RowType 'Property' rows carry the per-property
// columns; RowType 'Portfolio' rows carry Measure / Numerator / Denominator and
// must never reach the grid.
const isPresent = value => value !== null && value !== undefined && value !== "";

// NULL is not zero on this screen. OccupancyPercent NULL means the property was
// eligible on no day of the window, IsOccupiedToday NULL means it is not
// eligible today, and CurrentVacancyDays NULL means the same or that it was
// never occupied inside the window. None of the three is a vacancy.
// Per-property occupancy reads to one decimal; the two portfolio figures carry a
// second, because they are the headline numbers and 54.82 is quoted as such in
// docs/OCCUPANCY-RULES-01.md.
const percentText = value => (isPresent(value) ? Number(value).toFixed(1) + "%" : "N/A");
const portfolioPercentText = value => (isPresent(value) ? Number(value).toFixed(2) + "%" : "N/A");
const countText = value => (isPresent(value) ? Number(value).toLocaleString() : "N/A");

function Occupancy() {
    const currentYear = new Date().getFullYear();

    // chosen stays false until the operator picks a year, so the first read
    // sends no year at all and the server applies its own current calendar year.
    const [selection, setSelection] = useState({year: currentYear, chosen: false});
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [currentTab, setCurrentTab] = useState(TAB_ALL);
    const [height, setHeight] = useState(MIN_GRID_HEIGHT);

    // The block the grid and its footnote sit in, and the footnote itself.
    const gridAreaRef = useRef(null);
    const noteRef = useRef(null);
    // Smallest height computed since the last deliberate chrome change. The
    // footnote rewraps when the page scrollbar appears or disappears, and its
    // height feeds the next measurement, so a grid sized to exactly fill the
    // viewport can flip between two values forever. Keeping the floor breaks
    // that: a grid one footnote-line short never brings the scrollbar back.
    const floorRef = useRef(Infinity);

    const years = useMemo(() => {
        const list = [];
        for (let y = currentYear; y >= FIRST_YEAR; y--)
            list.push(y);
        return list;
    }, [currentYear]);

    const measureGrid = useCallback(() => {
        const area = gridAreaRef.current;
        if (!area) return;

        const viewport = document.documentElement.clientHeight;
        const scrollY = window.scrollY;

        const nav = document.getElementsByClassName("bottom-navigation")[0];
        // The bar is fixed-bottom and overlays the page, so its height has to
        // come off the grid rather than out of the document flow. Measured, not
        // assumed: it is taller once the navbar collapses below lg.
        const navHeight = nav ? nav.offsetHeight : CONTENT_TAIL;

        // Where the grid starts, and how much page sits below its last pixel -
        // the footnote, the card's own padding and border, and the wrapper's
        // padding. All read off the rendered elements at the same instant,
        // which is what makes them independent of the height the grid currently
        // happens to have: two rects taken together are right whatever that
        // height is, so a stale height cannot feed itself back in.
        //
        // This screen used to count the top down from the grid AREA, and the
        // area is .tab-content pt-1 - so its border box starts four pixels
        // above the grid, and those four pixels were four of the five pixels of
        // page scrollbar docs/OCCUPANCY-UI-03.md left outstanding. The fifth was
        // the card's 1px dashboard-border, which CARD_PADDING does not describe.
        // Measuring the grid and the card removes both constants rather than
        // adding a sixth one.
        //
        // Document scrollHeight is deliberately NOT used for this. The content
        // wrapper carries min-height: calc(100vh - 60px) (global.scss:10-13),
        // so a short page is pinned to the viewport and scrollHeight stops
        // describing the content - it would report a tail that is mostly empty
        // space and drive the grid smaller on every pass.
        const grid = area.querySelector(".dx-datagrid");
        const note = noteRef.current;
        const card = area.closest(".card");
        const wrapper = area.closest(".main-wrapper");

        let top;
        let toCardBottom;    // grid bottom -> last drawn pixel of the card
        let toWrapperBottom; // grid bottom -> end of the page's flow content
        if (grid && card && wrapper) {
            const rect = grid.getBoundingClientRect();
            // Document relative, not viewport relative. A page that is currently
            // scrolled would otherwise feed its own scroll offset back into the
            // next height and grow without bound.
            top = rect.top + scrollY;
            toCardBottom = card.getBoundingClientRect().bottom - rect.bottom;
            toWrapperBottom = wrapper.getBoundingClientRect().bottom - rect.bottom;
        } else {
            // No grid yet - loading, an error, or a year with no eligible
            // property. Fall back to the area and the named paddings so the
            // first pass still lands close, and let the pass after the grid
            // draws correct it.
            const padTop = parseFloat(window.getComputedStyle(area).paddingTop) || 0;
            top = area.getBoundingClientRect().top + scrollY + padTop;
            toCardBottom = (note ? note.offsetHeight : 0) + NOTE_MARGIN + CARD_PADDING;
            toWrapperBottom = toCardBottom + WRAPPER_PADDING;
        }

        // Two separate constraints, and the grid has to satisfy both. The
        // document must not outgrow the viewport, which costs the content
        // wrapper's tail padding on top of everything in the flow; and the last
        // drawn pixel of the card must clear the fixed bar, which costs the
        // bar's height instead. The wrapper's own trailing padding is empty, so
        // the bar may overlap it - the card border may not.
        const reserve = Math.max(toWrapperBottom + CONTENT_TAIL, toCardBottom + navHeight);

        // Floor, not round. A half pixel of line box left over is a page
        // scrollbar, and one pixel short of the viewport is invisible.
        let next = Math.floor(viewport - top - reserve);
        next = Math.min(next, floorRef.current);
        floorRef.current = next;
        next = Math.max(MIN_GRID_HEIGHT, next);
        setHeight(prev => (prev === next ? prev : next));
    }, []);

    // No dependency list. Every state this component holds moves the chrome
    // above the grid - the summary figures, the tab strip, the loading and
    // error bodies - and the effect settles to a fixed point after one further
    // pass, because a matching height sets no state.
    useLayoutEffect(() => {
        measureGrid();
    });

    useEffect(() => {
        const onResize = () => {
            floorRef.current = Infinity;
            measureGrid();
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [measureGrid]);

    useEffect(() => {
        // cancelled guards every setState below, so navigating away mid-request
        // cannot land an update on an unmounted component.
        let cancelled = false;
        setLoading(true);
        setError("");
        // A new read replaces the summary figures and the tab strip, so the
        // floor is released and re-established against the new chrome.
        floorRef.current = Infinity;
        LoaderService.setData(true);

        const url = selection.chosen
            ? "/Property/GetPropertyOccupancy?" + new URLSearchParams({year: selection.year})
            : "/Property/GetPropertyOccupancy";

        apiClient
            .get(url)
            .then(response => {
                LoaderService.setData(false);
                if (cancelled) return;
                try {
                    // Double encoded: the body is a JSON string holding the payload.
                    const payload = JSON.parse(response.data);
                    const message = payload[1] ? payload[1]["Message"] : "";
                    if (message) {
                        // Business failure still arrives as HTTP 200.
                        console.log("GetPropertyOccupancy: " + message);
                        setRows([]);
                        setError(message);
                    } else {
                        const data = payload[0] ? payload[0]["Data"] : null;
                        setRows(Array.isArray(data) ? data : []);
                        // Height is not set here. The layout effect measures it
                        // after the summary strip and the tab strip have drawn,
                        // which is the only point at which the space left for
                        // the grid is actually known.
                    }
                } catch (e) {
                    console.log(e);
                    setRows([]);
                    setError("Occupancy data could not be read. See the browser console for details.");
                }
                setLoading(false);
            })
            .catch(err => {
                // No alert - the failure is rendered in the page body instead.
                LoaderService.setData(false);
                console.log(err);
                if (cancelled) return;
                setRows([]);
                setError("Occupancy could not be loaded. See the browser console for details.");
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [selection]);

    // RowType is filtered here, before anything binds, and a row carrying no
    // ECO_ID is dropped with it. ECO_ID is the grid key: a keyless row still
    // draws - full height, banded, every cell border present and no content in
    // any of them - which is the blank leading row that appeared above the first
    // property. Nothing but a keyed Property row reaches the grid now.
    const propertyRows = useMemo(
        () => rows.filter(r => r["RowType"] === "Property" && isPresent(r["ECO_ID"])),
        [rows]);
    const byUnitCount = useMemo(
        () => rows.find(r => r["RowType"] === "Portfolio" && r["Measure"] === "ByUnitCount"),
        [rows]);
    const byDayCount = useMemo(
        () => rows.find(r => r["RowType"] === "Portfolio" && r["Measure"] === "ByDayCount"),
        [rows]);

    // Company tabs, derived from the rows already loaded. The property listing
    // takes its tabs from the server instead - Utilities.AccountsCountQuery, read
    // at Property/index.js:66 - but that query counts Property.Sold = 0 only, and
    // this screen deliberately keeps sold properties inside their ownership
    // window. Deriving here keeps the counts and the grid the same population.
    // OwnerECO_ID / OwnerName arrive on each Property row from
    // PropertyOccupancy.txt, resolved by the relationship the listing tabs use.
    const tabs = useMemo(() => {
        const companies = new Map();
        let unassigned = 0;
        propertyRows.forEach(row => {
            if (!isPresent(row["OwnerECO_ID"])) {
                // Never silently dropped - it gets its own tab so the counts
                // always reconcile back to the ALL count.
                unassigned++;
                return;
            }
            const key = String(row["OwnerECO_ID"]);
            const found = companies.get(key);
            if (found)
                found.count++;
            else
                companies.set(key, {
                    key: key,
                    name: isPresent(row["OwnerName"]) ? row["OwnerName"] : key,
                    count: 1
                });
        });
        // Same order as the listing tabs, which sort on the account name.
        const list = Array.from(companies.values()).sort((a, b) => a.name.localeCompare(b.name));
        list.unshift({key: TAB_ALL, name: "ALL", count: propertyRows.length});
        if (unassigned > 0)
            list.push({key: TAB_UNASSIGNED, name: "Unassigned", count: unassigned});
        return list;
    }, [propertyRows]);

    // Filtering is client side over rows already held. Changing tab must not
    // fire a request - only the year selector does that.
    const visibleRows = useMemo(() => {
        if (currentTab === TAB_ALL) return propertyRows;
        if (currentTab === TAB_UNASSIGNED) return propertyRows.filter(r => !isPresent(r["OwnerECO_ID"]));
        return propertyRows.filter(r => isPresent(r["OwnerECO_ID"]) && String(r["OwnerECO_ID"]) === currentTab);
    }, [propertyRows, currentTab]);

    useEffect(() => {
        // A tab that no longer exists in the loaded set falls back to ALL, so the
        // grid can never end up filtered by a company that is not on the strip.
        if (!tabs.some(t => t.key === currentTab))
            setCurrentTab(TAB_ALL);
    }, [tabs, currentTab]);

    const yearChanged = e => {
        if (!e.event) return;
        if (!isPresent(e.value)) return;
        setSelection({year: e.value, chosen: true});
    };

    // NULL sorts last in both directions. Without this the three properties that
    // are eligible on no day of the window head the descending sort.
    const sortPercent = row => (isPresent(row["OccupancyPercent"]) ? Number(row["OccupancyPercent"]) : -1);

    // The two-line treatment the property listing uses, reproduced rather than
    // reinvented: PropertiesComponent.js:436-441 renders the name in bold on the
    // first line and the second field beneath it, as two stacked col-12 divs.
    // That screen registers the markup as the DevExtreme "propertyNameTemplate"
    // and a VM_Column row selects it; this grid has no VM_Layout row to hang a
    // template on, so the same markup is produced through cellRender. Code is
    // display only - it is not unique, 001 and 002 each cover two rows, and
    // ECO_ID remains the grid key.
    const PropertyNameCell = props => (
        <div>
            <div className="col-12"><b>{props.data["Name"]}</b></div>
            <div className="col-12">{props.data["Code"]}</div>
        </div>
    );

    const OccupancyPercentCell = props => <span>{percentText(props.data["OccupancyPercent"])}</span>;

    const OccupiedTodayCell = props => {
        const value = props.data["IsOccupiedToday"];
        if (!isPresent(value))
            // Not eligible today. Deliberately not "Vacant".
            return (<label className="form-check-label"><span className="status-color na"></span>N/A</label>);
        if (value === true || value === 1)
            return (<label className="form-check-label"><span className="status-color occupied"></span>Occupied</label>);
        return (<label className="form-check-label"><span className="status-color unoccupied"></span>Vacant</label>);
    };

    const VacancyDaysCell = props => <span>{countText(props.data["CurrentVacancyDays"])}</span>;

    // Both figures are portfolio-wide and stay portfolio-wide: they are computed
    // server side over every property, so the company tab above the grid does not
    // rescope them. The headings say so.
    const summaryCard = (heading, measure, note) => (
        <Card className="card-light p-3 border-0 h-100">
            <div className="fs-4 fw-bold mb-1">{portfolioPercentText(measure ? measure["OccupancyPercent"] : null)}</div>
            <div className="text-secondary text-uppercase">{heading}</div>
            <div className="text-secondary fs-7 mt-1">
                {measure && isPresent(measure["Numerator"]) && isPresent(measure["Denominator"])
                    ? countText(measure["Numerator"]) + " of " + countText(measure["Denominator"]) + " " + note
                    : note}
            </div>
        </Card>
    );

    const gridBody = () => {
        if (loading)
            return <div className="text-secondary p-3">Loading occupancy&hellip;</div>;
        if (error)
            return (
                <div className="text-danger p-3" role="alert">
                    <div className="fw-bold mb-1">Occupancy could not be loaded.</div>
                    <div>{error}</div>
                </div>
            );
        if (propertyRows.length === 0)
            return <div className="text-secondary p-3">No properties were eligible in {selection.year}.</div>;
        if (visibleRows.length === 0)
            return <div className="text-secondary p-3">No properties are held under this company.</div>;
        return (
            <DataGrid
                dataSource={visibleRows}
                keyExpr="ECO_ID"
                height={height}
                showBorders={true}
                showRowLines={true}
                showColumnLines={true}
                columnAutoWidth={true}
                allowColumnResizing={true}
                columnResizingMode="widget"
                rowAlternationEnabled={true}
                hoverStateEnabled={true}
                wordWrapEnabled={true}
            >
                <Paging enabled={false}/>
                <Column caption="Property" dataField="Name" dataType="string" minWidth={260}
                        cellRender={PropertyNameCell}/>
                <Column caption="Eligible Days" dataField="EligibleDays" dataType="number"
                        format="#,##0" alignment="right" width={130}/>
                <Column caption="Occupied Days" dataField="OccupiedDays" dataType="number"
                        format="#,##0" alignment="right" width={130}/>
                <Column caption="Occupancy" dataField="OccupancyPercent" dataType="number"
                        alignment="right" width={130}
                        defaultSortIndex={0} defaultSortOrder="desc"
                        calculateSortValue={sortPercent}
                        cellRender={OccupancyPercentCell}/>
                <Column caption="Occupied Today" dataField="IsOccupiedToday" dataType="boolean"
                        alignment="left" width={150}
                        cellRender={OccupiedTodayCell}/>
                <Column caption="Current Vacancy Days" dataField="CurrentVacancyDays" dataType="number"
                        alignment="right" width={170}
                        cellRender={VacancyDaysCell}/>
            </DataGrid>
        );
    };

    return (
        <>
            <div
                className="search-actions-header d-flex flex-wrap flex-lg-nowrap justify-content-between align-items-center px-3 pt-3 p-lg-3 bg-white border-bottom">
                <div className="d-sm-flex justify-content-between align-items-center flex-fill mb-3 mb-lg-0">
                    <h2 className="fs-5 fw-medium mb-3 mb-sm-0 flex-shrink-0 me-4">Occupancy</h2>
                    <div className="d-flex align-items-center justify-content-sm-end flex-fill me-lg-1">
                        <span className="text-secondary flex-shrink-0 me-2">Calendar year</span>
                        <InputGroup className="me-3 search-input">
                            <SelectBox items={years}
                                       value={selection.year}
                                       inputAttr={{class: "form-control"}}
                                       elementAttr={{class: "search-input input-group"}}
                                       width="100%"
                                       onValueChanged={yearChanged}
                            ></SelectBox>
                        </InputGroup>
                    </div>
                </div>
            </div>
            <div className="main-wrapper p-3">
                <Card className="p-3 border-0 dashboard-border">
                    <div className="row mb-3">
                        <div className="col-12 col-xl-6 mb-3 mb-xl-0">
                            {summaryCard("Portfolio occupancy by unit count (today, all companies)", byUnitCount,
                                "properties occupied of those eligible today")}
                        </div>
                        <div className="col-12 col-xl-6">
                            {summaryCard("Portfolio occupancy by day count (" + selection.year + ", all companies)",
                                byDayCount, "occupied days of eligible days")}
                        </div>
                    </div>
                    <div
                        className="d-flex flex-wrap flex-xl-nowrap justify-content-between align-items-center mb-0 mb-md-4">
                        {/* Tabs title */}
                        <ul className="nav nav-tabs fs-6 nav-tab tab-sm inner-tab mb-3 mb-xl-0">
                            {tabs.map(item => {
                                return (<li key={item.key} className="nav-item" onClick={() => {
                                    setCurrentTab(item.key)
                                }}>
                                    <a className={currentTab === item.key ? "nav-link active" : "nav-link"}
                                       data-bs-toggle="tab"
                                       eventKey={item.key}
                                       href={'#' + item.key}>{item.name}<span
                                        className="tab-count"
                                    >{item.count}</span></a>
                                </li>)
                            })}
                        </ul>
                    </div>
                    <div className="tab-content pt-1" ref={gridAreaRef}>
                        {gridBody()}
                        <div className="text-secondary fs-7 mt-2" ref={noteRef}>
                            Occupancy is derived from tenancy contracts and short-term rental bookings, unioned at day
                            grain. The Available flag has no history, so today's value is applied to past windows. The
                            two portfolio figures above are separate measures, are computed across the whole portfolio
                            and do not rescope to the selected company tab; neither corrects the other. This screen
                            keeps sold properties inside their ownership window, so its counts run ahead of the property
                            listing, which shows unsold properties only.
                        </div>
                    </div>
                </Card>
            </div>
        </>
    );
}

export default Occupancy;
